import { queryClient } from './query-client';

type Cleanup = () => void;

const registry = new Map<string, Cleanup>();

// Census of docs held per live subscription — the retained-working-set proxy.
// Kept dependency-free (a Map.set per snapshot is negligible next to converter
// work) so the registry stays a plain data structure; readers do the gating.
type CensusEntry = {
  collection: string;
  docs: number;
};

const census = new Map<string, CensusEntry>();

let cacheSubscriberAttached = false;

// TanStack 5.0.0's queryFn AbortSignal never fires after the queryFn resolves, so a single shared
// cache subscriber tears down live Firestore listeners when their query leaves the cache.
const attachCacheSubscriberOnce = () => {
  if (cacheSubscriberAttached) {
    return;
  }
  cacheSubscriberAttached = true;
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'removed') {
      return;
    }
    const cleanup = registry.get(event.query.queryHash);
    if (!cleanup) {
      return;
    }
    registry.delete(event.query.queryHash);
    census.delete(event.query.queryHash);
    cleanup();
  });
};

export const recordSubscriptionDocCount = (
  queryHash: string,
  collection: string,
  docs: number,
): void => {
  census.set(queryHash, { collection, docs });
};

export const getSubscriptionCensus = () => {
  const byCollection: Record<string, { count: number; docs: number }> = {};
  let totalDocs = 0;

  census.forEach(({ collection, docs }) => {
    const aggregate = byCollection[collection] ?? { count: 0, docs: 0 };
    aggregate.count += 1;
    aggregate.docs += docs;
    byCollection[collection] = aggregate;
    totalDocs += docs;
  });

  return { live: registry.size, totalDocs, byCollection };
};

export const registerSubscription = (
  queryHash: string,
  cleanup: Cleanup,
): void => {
  attachCacheSubscriberOnce();
  const previous = registry.get(queryHash);
  if (previous) {
    previous();
  }
  registry.set(queryHash, cleanup);
};

export const unregisterSubscription = (
  queryHash: string,
  cleanup: Cleanup,
): void => {
  if (registry.get(queryHash) !== cleanup) {
    return;
  }
  registry.delete(queryHash);
  census.delete(queryHash);
};

export const clearAllSubscriptions = (): void => {
  registry.forEach((cleanup) => cleanup());
  registry.clear();
  census.clear();
};

export const getLiveSubscriptionCount = (): number => registry.size;
